import { ButtonWithDropdown } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { AddIcon } from '@ardoq/icons';
import { GroupType, ReferenceDirection } from '@ardoq/api-types';
import { v4 as uuidv4 } from 'uuid';
import {
  PerspectiveGroupingRule,
  PerspectivesGroupsOptions,
  PerspectivesGroupsWorkspace,
} from './types';
import {
  DisabledOptionProps,
  EntitiesWithPossibleDisabledOptionsName,
} from '../types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

const getWorkspaceIdForNewGroup = (
  option: Record<string, any[]>,
  workspaces: PerspectivesGroupsWorkspace[],
  shouldDisableIrrelevantOptions?: boolean
) =>
  shouldDisableIrrelevantOptions
    ? workspaces.find(({ id }) => option[id]?.length)?.id || workspaces[0]?.id
    : workspaces[0]?.id;

const getNewGroupDefaultValues = (
  groupOptions: PerspectivesGroupsOptions,
  disableIrrelevantOptions?: boolean
) => {
  const defaultWorkspaceIdForGroupByParent = groupOptions.workspaces?.[0]?.id;

  const defaultWorkspaceIdForGroupingByReference = getWorkspaceIdForNewGroup(
    groupOptions.referenceTypesPerWorkspace,
    groupOptions.workspaces,
    disableIrrelevantOptions
  );

  const defaultWorkspaceIdForGroupingByField = getWorkspaceIdForNewGroup(
    groupOptions.fieldsPerWorkspace,
    groupOptions.workspaces,
    disableIrrelevantOptions
  );

  const defaultWorkspaceIdForGroupingByTag = getWorkspaceIdForNewGroup(
    groupOptions.tagsPerWorkspace,
    groupOptions.workspaces,
    disableIrrelevantOptions
  );

  const defaultComponentTypeId =
    groupOptions.componentTypesPerWorkspace[
      defaultWorkspaceIdForGroupByParent
    ]?.[0]?.id;

  const defaultReferenceType =
    groupOptions.referenceTypesPerWorkspace[
      defaultWorkspaceIdForGroupingByReference
    ]?.[0];

  const defaultFieldId =
    groupOptions.fieldsPerWorkspace[defaultWorkspaceIdForGroupingByField]?.[0]
      ?.id;

  const defaultTagId =
    groupOptions.tagsPerWorkspace[defaultWorkspaceIdForGroupingByTag]?.[0]?.id;

  const defaultReferenceTypeName =
    groupOptions.globalReferenceTypeNames?.[0] ?? null;

  return {
    defaultWorkspaceIdForGroupByParent,
    defaultWorkspaceIdForGroupingByReference,
    defaultWorkspaceIdForGroupingByField,
    defaultWorkspaceIdForGroupingByTag,
    defaultComponentTypeId,
    defaultReferenceType,
    defaultFieldId,
    defaultTagId,
    defaultReferenceTypeName,
  };
};

type AddPerspectiveGroupButtonProps = {
  groupingOptions: PerspectivesGroupsOptions;
  onGroupingRuleAdded: (newGroupingRule: PerspectiveGroupingRule) => void;
  disabledOptionProps?: DisabledOptionProps;
  buttonLabel?: string;
  isDisabled?: boolean;
  /**
   * Whether the "By Reference Type Name" grouping option should be available.
   * Wired from the consuming app's feature flag (Features.GROUPING_BY_TYPE_NAMES)
   * to keep this package free of the deprecated @ardoq/features singleton.
   */
  isGroupingByTypeNamesEnabled?: boolean;
  i18nLocale?: I18nLocale;
};
const AddPerspectiveGroupButton = ({
  groupingOptions,
  onGroupingRuleAdded,
  buttonLabel,
  disabledOptionProps,
  isDisabled,
  isGroupingByTypeNamesEnabled = false,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: AddPerspectiveGroupButtonProps) => {
  const resolvedButtonLabel =
    buttonLabel ??
    t(
      i18nLocale,
      'perspectives:addPerspectiveGroupButton.addGroup',
      'Add group'
    );
  const { getAddButtonDisabledPopoverContent } = disabledOptionProps || {};
  const shouldDisableIrrelevantOptions = Boolean(disabledOptionProps);

  const getDisabledPopoverProps = (
    entitiesName: EntitiesWithPossibleDisabledOptionsName,
    isDisabled: boolean
  ) =>
    getAddButtonDisabledPopoverContent && isDisabled
      ? {
          content: getAddButtonDisabledPopoverContent(entitiesName),
        }
      : undefined;

  const {
    defaultWorkspaceIdForGroupByParent,
    defaultWorkspaceIdForGroupingByTag,
    defaultWorkspaceIdForGroupingByField,
    defaultWorkspaceIdForGroupingByReference,
    defaultComponentTypeId,
    defaultReferenceType,
    defaultFieldId,
    defaultTagId,
    defaultReferenceTypeName,
  } = getNewGroupDefaultValues(groupingOptions, shouldDisableIrrelevantOptions);

  const isPossibleToCreateReferenceGroupingRule = shouldDisableIrrelevantOptions
    ? Object.values(groupingOptions.referenceTypesPerWorkspace).some(
        referenceTypes => referenceTypes.length
      )
    : true;
  const isPossibleCreateFieldGroupingRule = shouldDisableIrrelevantOptions
    ? Object.values(groupingOptions.fieldsPerWorkspace).some(
        fields => fields.length
      )
    : true;
  const isPossibleCreateTagGroupingRule = shouldDisableIrrelevantOptions
    ? Object.values(groupingOptions.tagsPerWorkspace).some(tags => tags.length)
    : true;
  const isPossibleCreateParentGroupingRule = shouldDisableIrrelevantOptions
    ? Object.values(groupingOptions.componentTypesPerWorkspace).some(
        componentTypes => componentTypes.length
      )
    : true;

  const isPossibleToCreateReferenceTypeNameGroupingRule =
    shouldDisableIrrelevantOptions
      ? groupingOptions.globalReferenceTypeNames?.length > 0
      : true;

  return (
    <ButtonWithDropdown
      dataTestId="create-grouping-rule"
      dropdownSize={DropdownSize.S}
      isDisabled={isDisabled}
      options={[
        {
          label: t(
            i18nLocale,
            'perspectives:addPerspectiveGroupButton.byWorkspace',
            'By Workspace'
          ),
          onClick: () =>
            onGroupingRuleAdded({
              type: GroupType.WORKSPACE,
              id: uuidv4(),
            }),
        },
        {
          label: t(
            i18nLocale,
            'perspectives:addPerspectiveGroupButton.byReference',
            'By Reference'
          ),
          isDisabled: !isPossibleToCreateReferenceGroupingRule,
          popoverProps: getDisabledPopoverProps(
            'references',
            !isPossibleToCreateReferenceGroupingRule
          ),
          onClick: () =>
            onGroupingRuleAdded({
              id: uuidv4(),
              type: GroupType.REFERENCE,
              workspaceId: defaultWorkspaceIdForGroupingByReference,
              targetId: defaultReferenceType?.id?.toString(),
              direction: ReferenceDirection.INCOMING,
              sublabel: '',
            }),
        },
        ...(isGroupingByTypeNamesEnabled
          ? [
              {
                label: t(
                  i18nLocale,
                  'perspectives:addPerspectiveGroupButton.byReferenceTypeName',
                  'By Reference Type Name'
                ),
                isDisabled: !isPossibleToCreateReferenceTypeNameGroupingRule,
                popoverProps: getDisabledPopoverProps(
                  'references',
                  !isPossibleToCreateReferenceTypeNameGroupingRule
                ),
                onClick: () =>
                  onGroupingRuleAdded({
                    id: uuidv4(),
                    type: GroupType.REFERENCE_TYPE_NAME,
                    targetId: defaultReferenceTypeName ?? '',
                    direction: ReferenceDirection.INCOMING,
                    sublabel: '',
                  }),
              },
            ]
          : []),
        {
          label: t(
            i18nLocale,
            'perspectives:addPerspectiveGroupButton.byComponentTypes',
            'By Component Types'
          ),
          onClick: () =>
            onGroupingRuleAdded({
              id: uuidv4(),
              type: GroupType.COMPONENT,
            }),
        },
        {
          label: t(
            i18nLocale,
            'perspectives:addPerspectiveGroupButton.byParent',
            'By Parent'
          ),
          isDisabled: !isPossibleCreateParentGroupingRule,
          popoverProps: getDisabledPopoverProps(
            'components',
            !isPossibleCreateParentGroupingRule
          ),
          onClick: () =>
            onGroupingRuleAdded({
              id: uuidv4(),
              type: GroupType.PARENT,
              workspaceId: defaultWorkspaceIdForGroupByParent,
              targetId: defaultComponentTypeId,
              sublabel: '',
            }),
        },
        {
          label: t(
            i18nLocale,
            'perspectives:addPerspectiveGroupButton.byParentAll',
            'By Parent All'
          ),
          onClick: () =>
            onGroupingRuleAdded({
              id: uuidv4(),
              type: GroupType.PARENT_ALL,
              sublabel: '',
            }),
        },
        {
          label: t(
            i18nLocale,
            'perspectives:addPerspectiveGroupButton.byField',
            'By Field'
          ),
          isDisabled: !isPossibleCreateFieldGroupingRule,
          popoverProps: getDisabledPopoverProps(
            'fields',
            !isPossibleCreateFieldGroupingRule
          ),
          onClick: () =>
            onGroupingRuleAdded({
              id: uuidv4(),
              type: GroupType.FIELD,
              workspaceId: defaultWorkspaceIdForGroupingByField,
              targetId: defaultFieldId,
            }),
        },
        {
          label: t(
            i18nLocale,
            'perspectives:addPerspectiveGroupButton.byTag',
            'By Tag'
          ),
          isDisabled: !isPossibleCreateTagGroupingRule,
          popoverProps: getDisabledPopoverProps?.(
            'tags',
            !isPossibleCreateTagGroupingRule
          ),
          onClick: () =>
            onGroupingRuleAdded({
              id: uuidv4(),
              type: GroupType.TAG,
              workspaceId: defaultWorkspaceIdForGroupingByTag,
              targetId: defaultTagId,
            }),
        },
      ]}
    >
      <AddIcon />
      {resolvedButtonLabel}
    </ButtonWithDropdown>
  );
};

export { AddPerspectiveGroupButton };
